import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, MessageCircle, X, User } from 'lucide-react';

const GeminiChat = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) scrollToBottom();
    }, [messages, isOpen]);

    const handleSendMessage = async () => {
        if (!input.trim()) return;

        const newUserMessage = { role: 'user', content: input };
        setMessages((prevMessages) => [...prevMessages, newUserMessage]);
        setInput('');
        setLoading(true);

        const prompt =
            'أنت مساعد صيدلاني افتراضي. قدم استشارة طبية عامة ودقيقة بناءً على أسئلة المستخدم. اجعل إجاباتك ودية ومفيدة. لا تقدم تشخيصًا طبيًا نهائيًا، بل انصح المستخدم دائمًا بزيارة الطبيب المختص للحصول على تشخيص دقيق. استخدم اللغة العربية الفصحى. السؤال هو: ' +
            input;

        let retries = 0;
        const maxRetries = 5;
        const baseDelay = 1000;

        const sendMessageToGemini = async () => {
            const apiKey = '';
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent?key=${apiKey}`;

            const payload = {
                contents: [{ role: 'user', parts: [{ text: prompt }] }]
            };

            try {
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();

                if (
                    result.candidates &&
                    result.candidates.length > 0 &&
                    result.candidates[0].content &&
                    result.candidates[0].content.parts &&
                    result.candidates[0].content.parts.length > 0
                ) {
                    const geminiMessage = result.candidates[0].content.parts[0].text;
                    setMessages((prevMessages) => [...prevMessages, { role: 'bot', content: geminiMessage }]);
                } else {
                    throw new Error('Invalid API response structure');
                }
            } catch (error) {
                console.error('Failed to fetch from Gemini API:', error);
                if (retries < maxRetries) {
                    retries++;
                    const delay = baseDelay * Math.pow(2, retries);
                    await new Promise((res) => setTimeout(res, delay));
                    await sendMessageToGemini();
                } else {
                    setMessages((prevMessages) => [
                        ...prevMessages,
                        { role: 'bot', content: 'عذراً، حدث خطأ أثناء الاتصال. يرجى المحاولة لاحقاً.' }
                    ]);
                }
            } finally {
                setLoading(false);
            }
        };

        sendMessageToGemini();
    };

    return (
        <div className="fixed bottom-6 left-6 z-50 font-['Cairo']" dir="rtl">
            {/* نافذة المحادثة */}
            {isOpen && (
                <div className="mb-4 flex h-[28rem] w-80 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-100 sm:w-96">
                    {/* رأس النافذة */}
                    <div className="flex items-center justify-between bg-gradient-to-l from-[#10B981] to-[#0ea173] px-4 py-3.5 text-white">
                        <div className="flex items-center gap-2.5">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                                <Bot className="h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold leading-tight">المساعد الصيدلاني</h3>
                                <p className="flex items-center gap-1 text-xs text-white/80">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                                    متصل الآن
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="rounded-full p-1.5 transition-colors duration-200 hover:bg-white/15"
                            aria-label="إغلاق المحادثة"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* الرسائل */}
                    <div className="flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4 text-right">
                        {messages.length === 0 && (
                            <div className="mt-8 text-center text-sm leading-relaxed text-gray-500">
                                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#10B981]/10">
                                    <Bot className="h-6 w-6 text-[#10B981]" />
                                </div>
                                مرحباً! أنا مساعدك الصيدلاني الافتراضي.
                                <br />
                                كيف يمكنني مساعدتك اليوم؟
                            </div>
                        )}
                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                {msg.role === 'bot' && (
                                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#10B981] text-white">
                                        <Bot className="h-3.5 w-3.5" />
                                    </div>
                                )}
                                <div
                                    className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
                                        msg.role === 'user'
                                            ? 'rounded-bl-md bg-[#10B981] text-white'
                                            : 'rounded-br-md bg-white text-gray-800 ring-1 ring-gray-100'
                                    }`}
                                >
                                    {msg.content}
                                </div>
                                {msg.role === 'user' && (
                                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-white">
                                        <User className="h-3.5 w-3.5" />
                                    </div>
                                )}
                            </div>
                        ))}
                        {loading && (
                            <div className="flex items-end justify-start gap-2">
                                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#10B981] text-white">
                                    <Bot className="h-3.5 w-3.5" />
                                </div>
                                <div className="flex items-center gap-1 rounded-2xl rounded-br-md bg-white px-4 py-3 shadow-sm ring-1 ring-gray-100">
                                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
                                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
                                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* حقل الإدخال */}
                    <div className="border-t border-gray-100 bg-white p-3">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && !loading && handleSendMessage()}
                                placeholder="اكتب رسالتك..."
                                className="flex-1 rounded-full border-2 border-gray-200 px-4 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-[#10B981]"
                                disabled={loading}
                            />
                            <button
                                onClick={handleSendMessage}
                                className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white transition-all duration-200 ${
                                    loading
                                        ? 'cursor-not-allowed bg-gray-300'
                                        : 'bg-[#10B981] hover:bg-[#0ea173] active:scale-95'
                                }`}
                                disabled={loading}
                                aria-label="إرسال"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* أيقونة تشغيل المحادثة العائمة */}
            <button
                onClick={() => setIsOpen((prev) => !prev)}
                className="group relative flex h-16 w-16 items-center justify-center rounded-full bg-[#10B981] text-white shadow-lg shadow-[#10B981]/40 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#10B981]/50 active:scale-95"
                aria-label={isOpen ? 'إغلاق المحادثة' : 'افتح المساعد الصيدلاني'}
            >
                {!isOpen && (
                    <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#10B981]/60" />
                )}
                {isOpen ? <X className="h-7 w-7" /> : <MessageCircle className="h-7 w-7" />}
            </button>
        </div>
    );
};

export default GeminiChat;
